import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaCalculator, FaTimes, FaExchangeAlt, FaEquals } from 'react-icons/fa';

// Exchange rate data (mock - in a real app, fetch from an API like exchangerate-api.com)
const EXCHANGE_RATES = {
  USD: 1,
  NGN: 1400,  // Nigerian Naira
  GBP: 0.78,  // British Pound
  EUR: 0.92,  // Euro
  CNY: 7.25,  // Chinese Yuan (RMB)
  JPY: 150.50,// Japanese Yen
  CAD: 1.36,  // Canadian Dollar
  AUD: 1.50,  // Australian Dollar
  INR: 83.40  // Indian Rupee
};

type CalculatorMode = 'standard' | 'scientific' | 'currency';
type Currency = keyof typeof EXCHANGE_RATES;

const CalculatorWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [result, setResult] = useState('');
  const [mode, setMode] = useState<CalculatorMode>('standard');
  const [fromCurrency, setFromCurrency] = useState<Currency>('USD');
  const [toCurrency, setToCurrency] = useState<Currency>('EUR');
  const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const calculatorRef = useRef<HTMLDivElement>(null);
  
  // Memoize currencies to prevent unnecessary re-renders
  const currencies = useMemo(() => Object.keys(EXCHANGE_RATES) as Currency[], []);

  // Close calculator when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calculatorRef.current && !calculatorRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const calculate = useCallback((): void => {
    try {
      if (!input) return;
      
      // Replace math symbols for evaluation
      const expression = input
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
        .replace(/%/g, '/100')
        .replace(/√\(([^)]+)\)/g, 'Math.sqrt($1)')
        .replace(/\^/g, '**')
        .replace(/log\(/g, 'Math.log10(')
        .replace(/ln\(/g, 'Math.log(')
        .replace(/sin\(/g, 'Math.sin(')
        .replace(/cos\(/g, 'Math.cos(')
        .replace(/tan\(/g, 'Math.tan(')
        .replace(/PI/g, 'Math.PI')
        .replace(/E/g, 'Math.E');
      
      // eslint-disable-next-line no-eval -- input is the user's own typed expression; replace with a parser if this widget ever takes shared input
      const calculated = eval(expression);
      const resultStr = Number.isInteger(calculated) ? calculated.toString() : calculated.toFixed(8).replace(/\.?0+$/, '');
      
      setResult(resultStr);
      setHistory(prev => [...prev.slice(-4), `${input} = ${resultStr}`]);
      setInput(resultStr);
    } catch (error) {
      setResult('Error');
    }
  }, [input]);
  
  const handleCurrencyConversion = useCallback((): void => {
    if (!input || isNaN(Number(input))) return;
    
    const amount = parseFloat(input);
    const fromRate = EXCHANGE_RATES[fromCurrency];
    const toRate = EXCHANGE_RATES[toCurrency];
    
    // Convert to USD first, then to target currency
    const result = (amount / fromRate) * toRate;
    setConvertedAmount(parseFloat(result.toFixed(4)));
    setHistory(prev => [...prev.slice(-4), `${amount} ${fromCurrency} = ${result.toFixed(4)} ${toCurrency}`]);
  }, [input, fromCurrency, toCurrency]);
  
  const switchCurrencies = useCallback((): void => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    if (convertedAmount !== null) {
      setInput(convertedAmount.toString());
      setConvertedAmount(parseFloat(input));
    }
  }, [toCurrency, fromCurrency, convertedAmount, input]);

  const clear = useCallback((): void => {
    setInput('');
    setResult('');
    setConvertedAmount(null);
  }, []);

  const backspace = useCallback((): void => {
    setInput(prev => prev.slice(0, -1));
  }, []);
  
  const addToInput = useCallback((value: string): void => {
    setInput(prev => prev + value);
  }, []);
  
  const addFunction = useCallback((func: string): void => {
    setInput(prev => prev + func + '(');
  }, []);

  // Memoize calculator buttons to prevent unnecessary re-renders
  const scientificButtons = useMemo(() => [
    { label: '√', value: '√(', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'x²', value: '^2', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'x^y', value: '^', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'log', value: 'log', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'ln', value: 'ln', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'sin', value: 'sin', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'cos', value: 'cos', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'tan', value: 'tan', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'π', value: 'PI', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: 'e', value: 'E', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '(', value: '(', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: ')', value: ')', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
  ], []);

  const standardButtons = useMemo(() => [
    { label: '7', value: '7', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '8', value: '8', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '9', value: '9', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '÷', value: '÷', className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
    { label: '4', value: '4', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '5', value: '5', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '6', value: '6', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '×', value: '×', className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
    { label: '1', value: '1', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '2', value: '2', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '3', value: '3', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '-', value: '-', className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
    { label: '0', value: '0', className: 'col-span-2 bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '.', value: '.', className: 'bg-gray-100 text-black dark:bg-boxdark-2 dark:text-white' },
    { label: '+', value: '+', className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
  ], []);

  const actionButtons = useMemo(() => [
    { label: 'C', onClick: clear, className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
    { label: '⌫', onClick: backspace, className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
    { label: '%', onClick: () => addToInput('%'), className: 'bg-primary/10 text-primary-dark dark:bg-primary/20 dark:text-white' },
    { 
      label: mode === 'currency' ? '=' : <FaEquals className="mx-auto" />, 
      onClick: mode === 'currency' ? handleCurrencyConversion : calculate, 
      className: 'bg-primary text-white hover:bg-primary-dark' 
    },
  ], [mode, clear, backspace, addToInput, handleCurrencyConversion, calculate]);

  // Memoize the calculator button render functions
  const renderActionButtons = useCallback(() => {
    return actionButtons.map((btn, index: number) => (
      <button
        key={`action-${index}`}
        onClick={btn.onClick}
        className={`min-h-11 rounded p-2 text-sm font-medium hover:opacity-90 ${btn.className}`}
      >
        {btn.label}
      </button>
    ));
  }, [actionButtons]);

  const renderScientificButtons = useCallback(() => {
    return scientificButtons.map((btn, index: number) => (
      <button
        key={`sci-${index}`}
        onClick={() => btn.label.length > 2 ? addFunction(btn.value) : addToInput(btn.value)}
        className={`min-h-11 rounded p-2 text-sm hover:opacity-90 ${btn.className}`}
      >
        {btn.label}
      </button>
    ));
  }, [scientificButtons, addFunction, addToInput]);

  const renderStandardButtons = useCallback(() => {
    return standardButtons.map((btn, index: number) => (
      <button
        key={`std-${index}`}
        onClick={() => addToInput(btn.value)}
        className={`min-h-11 rounded p-2 text-sm hover:opacity-90 ${btn.className}`}
      >
        {btn.label}
      </button>
    ));
  }, [standardButtons, addToInput]);

  const renderCurrencyOptions = useCallback(() => {
    return currencies.map((curr) => (
      <option key={`opt-${curr}`} value={curr}>
        {curr}
      </option>
    ));
  }, [currencies]);

  return (
    <div className="relative" ref={calculatorRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-meta-4"
        aria-label="Calculator"
      >
        <FaCalculator className="text-xl text-gray-600 dark:text-gray-300" />
      </button>

      {isOpen && (
        <div className="fixed left-2 right-2 top-full mt-1 z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 max-h-[calc(100dvh-5rem)] overflow-y-auto rounded-lg border border-stroke bg-white shadow-lg dark:border-strokedark dark:bg-boxdark">
          <div className="flex items-center justify-between border-b border-stroke px-4 py-3 dark:border-strokedark">
            <div className="flex space-x-1">
              <button 
                onClick={() => setMode('standard')}
                className={`min-h-10 px-3 py-2 text-xs rounded ${mode === 'standard' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-boxdark-2'}`}
              >
                Standard
              </button>
              <button 
                onClick={() => setMode('scientific')}
                className={`min-h-10 px-3 py-2 text-xs rounded ${mode === 'scientific' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-boxdark-2'}`}
              >
                Scientific
              </button>
              <button 
                onClick={() => setMode('currency')}
                className={`min-h-10 px-3 py-2 text-xs rounded ${mode === 'currency' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-boxdark-2'}`}
              >
                Currency
              </button>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-meta-4"
              aria-label="Close calculator"
            >
              <FaTimes className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          <div className="p-4">
            {/* History */}
            {history.length > 0 && (
              <div className="mb-2 max-h-20 overflow-y-auto text-xs text-gray-600 dark:text-gray-300">
                {history.map((item, index) => (
                  <div key={index} className="text-right">{item}</div>
                ))}
              </div>
            )}
            
            {/* Currency Converter */}
            {mode === 'currency' && (
              <div className="mb-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <select
                    value={fromCurrency}
                    onChange={(e) => setFromCurrency(e.target.value as Currency)}
                    className="flex-1 min-h-10 rounded border border-stroke bg-white py-1 px-2 text-base sm:text-sm text-black dark:border-strokedark dark:bg-boxdark-2 dark:text-gray-300"
                  >
                    {renderCurrencyOptions()}
                  </select>
                  <button 
                    onClick={switchCurrencies}
                    className="rounded-full p-1 hover:bg-gray-100 dark:hover:bg-meta-4"
                    aria-label="Switch currencies"
                  >
                    <FaExchangeAlt className="h-4 w-4 text-gray-500" />
                  </button>
                  <select
                    value={toCurrency}
                    onChange={(e) => setToCurrency(e.target.value as Currency)}
                    className="flex-1 min-h-10 rounded border border-stroke bg-white py-1 px-2 text-base sm:text-sm text-black dark:border-strokedark dark:bg-boxdark-2 dark:text-gray-300"
                  >
                    {renderCurrencyOptions()}
                  </select>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Enter amount"
                    className="w-full rounded-lg border border-stroke bg-gray-50 p-3 pr-16 text-right text-lg font-semibold text-black dark:border-strokedark dark:bg-boxdark-2 dark:text-gray-300"
                  />
                  <span className="absolute right-3 top-3 text-gray-500 dark:text-gray-300">
                    {fromCurrency}
                  </span>
                </div>
                {convertedAmount !== null && (
                  <div className="rounded-lg border border-stroke bg-gray-50 p-3 text-right dark:border-strokedark dark:bg-boxdark-2">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {parseFloat(input || '0').toLocaleString()} {fromCurrency} =
                    </div>
                    <div className="text-lg font-semibold text-black dark:text-gray-300">
                      {convertedAmount.toLocaleString()} {toCurrency}
                    </div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
                      1 {fromCurrency} = {(EXCHANGE_RATES[toCurrency] / EXCHANGE_RATES[fromCurrency]).toFixed(6)} {toCurrency}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Standard/Scientific Calculator Display */}
            {mode !== 'currency' && (
              <div className="mb-4 rounded-lg bg-gray-50 p-3 dark:bg-boxdark-2">
                <div className="h-6 overflow-x-auto text-right text-sm text-gray-600 dark:text-gray-300">
                  {input || '0'}
                </div>
                <div className="h-8 text-right text-lg font-semibold text-black dark:text-gray-300">
                  {result || '0'}
                </div>
              </div>
            )}
        
            <div className="grid grid-cols-4 gap-2">
              {/* Action Buttons */}
              {renderActionButtons()}
              
              {/* Scientific Buttons */}
              {mode === 'scientific' && renderScientificButtons()}
              
              {/* Standard Buttons */}
              {mode !== 'currency' && renderStandardButtons()}
            </div>
          </div>
        </div>
      )}
    </div>
);
};

export default CalculatorWidget;